import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback, MutableRefObject, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { FormApi } from 'final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { getMeasureNumbers, setMeasureNumbersSearchQuery } from '../actions/ChangeKnoInMeasureActions';
import { getMeasureNumbers as selectMeasureNumbers } from '../selectors/changeKnoInMeasureSelectors';
import { IChangeKnoInMeasureForm } from '../../PersonalAccountPackage/AdminModule/interfaces/IChangeKnoInMeasureForm';
import { REQUIRED_ERROR } from '../../CorePackage/MainModule/constants/validationErrors';
import { TextInputField } from '../../UIPackage/MainModule/components/TextInputField';
import { IAutoCompleteItem } from '../../UIPackage/MainModule/interfaces/IAutoCompleteItem';
import { IMeasureNumberSearchItem } from '../../APIPackage/KnmModule/interfaces/IMeasureNumberSearchItem';

const SEARCH_TEXT_MIN_LENGTH = 3;

interface IMeasureNumberSearchProps {
  formRef: MutableRefObject<FormApi<IChangeKnoInMeasureForm, Partial<IChangeKnoInMeasureForm>> | undefined>;
}

interface IMeasureNumberSearchStoreProps {
  measureNumbers: IMeasureNumberSearchItem[];
}

const MeasureNumberSearchWithoutStore = ({
  formRef,
  measureNumbers,
}: IMeasureNumberSearchProps & IMeasureNumberSearchStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [options, setOptions] = useState<IAutoCompleteItem[] | undefined>();
  const [isSelected, setIsSelected] = useState(false);
  const [isFounded, setIsFounded] = useState(false);

  const findMeasures = useCallback(
    (searchText?: string) => {
      if (searchText && searchText.length >= SEARCH_TEXT_MIN_LENGTH) {
        setIsFounded(true);
        dispatch(setMeasureNumbersSearchQuery(searchText));
        dispatch(getMeasureNumbers());
      }
    },
    [dispatch],
  );

  const debouncedFindMeasures = useMemo(() => debounce(findMeasures, 1000), [findMeasures]);

  useEffect(() => () => debouncedFindMeasures.cancel(), [debouncedFindMeasures]);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const searchText = value?.trim();

      if (searchText) {
        formRef?.current?.resetFieldState('measureErpIdTitle');
      }

      formRef?.current?.change('measureErpId');
      formRef?.current?.change('measureSystem');

      if (!searchText) {
        setIsSelected(false);
      }
      if (!searchText || searchText.length < SEARCH_TEXT_MIN_LENGTH) {
        setOptions(undefined);
      } else {
        debouncedFindMeasures(searchText);
      }
    },
    [debouncedFindMeasures, formRef],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      const selected = measureNumbers.find(item => item.number === value);

      formRef?.current?.change('measureErpIdTitle', value);
      formRef?.current?.change('measureErpId', value);
      formRef?.current?.change('measureSystem', selected?.system);
      formRef?.current?.resetFieldState('measureErpIdTitle');

      debouncedFindMeasures('');
      setOptions(undefined);
      setIsSelected(true);
    },
    [debouncedFindMeasures, formRef, measureNumbers],
  );

  useEffect(() => {
    isFounded && setOptions(measureNumbers.map(n => ({ value: n.number, label: n.number })));
  }, [measureNumbers, isFounded]);

  const handleTextInputClear = useCallback(() => {
    setIsSelected(false);
    formRef?.current?.change('measureErpIdTitle');
    formRef?.current?.change('measureErpId');
    formRef?.current?.change('measureSystem');
  }, []);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedFindMeasures('');
        setOptions(undefined);
      }
    },
    [debouncedFindMeasures],
  );

  const validateMeasure = (value: string | undefined, allValues: IChangeKnoInMeasureForm) => {
    if (!value?.trim()) {
      return REQUIRED_ERROR;
    }

    if (!allValues.measureErpId) {
      return 'Мероприятие обязательно для выбора';
    }

    return undefined;
  };

  return (
    <div className="shared-row-field">
      <AutoComplete
        options={options}
        noFoundMessage="Мероприятия по номеру не найдены"
        handleAutoCompleteSelect={handleAutoCompleteSelect}
      >
        <TextInputField
          id="measureErpIdTitle"
          name="measureErpIdTitle"
          placeholder="Введите номер"
          clearable={isSelected}
          onChange={handleTextInputChange}
          onClear={handleTextInputClear}
          onBlur={handleTextInputBlur}
          validate={validateMeasure}
        />
      </AutoComplete>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IMeasureNumberSearchStoreProps => ({
  measureNumbers: selectMeasureNumbers(state),
});

export const MeasureNumberSearch = connect(mapStateToProps)(MeasureNumberSearchWithoutStore);
